import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Paper,
  Typography,
  Stack,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  EditRounded,
  DeleteOutlineRounded,
  ContentCopyRounded,
  CheckRounded,
  ApartmentRounded,
} from "@mui/icons-material";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import ConfirmDialog from "components/common/ConfirmDialog";
import {
  AAMC_SPECIALTIES,
  getSpecialtyLabel,
} from "shared/services/constants/specialties";
import {
  subscribeDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  buildCheckInUrl,
} from "services/clinicService";

const NONE_SPECIALTY = "";

const DepartmentsPage = ({ data }) => {
  const clinicId = data?.id;

  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createError, setCreateError] = useState(null);
  const [createdName, setCreatedName] = useState(null);
  const [creating, setCreating] = useState(false);

  const [editTarget, setEditTarget] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [copiedId, setCopiedId] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    defaultValues: { name: "", specialty: NONE_SPECIALTY },
    mode: "onChange",
  });

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEdit,
    formState: { isValid: editIsValid },
  } = useForm({
    defaultValues: { name: "", specialty: NONE_SPECIALTY },
    mode: "onChange",
  });

  useEffect(() => {
    if (!clinicId) return undefined;
    setLoading(true);
    const unsubscribe = subscribeDepartments(
      clinicId,
      (rows) => {
        setDepartments(rows);
        setLoading(false);
      },
      (err) => {
        console.error("[DepartmentsPage] departments subscription failed:", err);
        setLoading(false);
      },
    );
    return () => unsubscribe();
  }, [clinicId]);

  useEffect(() => {
    if (editTarget) {
      resetEdit({
        name: editTarget.name || "",
        specialty: editTarget.specialty || NONE_SPECIALTY,
      });
      setEditError(null);
    }
  }, [editTarget, resetEdit]);

  const onCreate = async (values) => {
    setCreating(true);
    setCreateError(null);
    setCreatedName(null);
    try {
      await createDepartment({
        clinicId,
        name: values.name,
        specialty: values.specialty || null,
      });
      setCreatedName(values.name.trim());
      reset({ name: "", specialty: NONE_SPECIALTY });
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const onSaveEdit = async (values) => {
    if (!editTarget) return;
    setSavingEdit(true);
    setEditError(null);
    try {
      await updateDepartment({
        clinicId,
        departmentId: editTarget.id,
        name: values.name,
        specialty: values.specialty || null,
      });
      setEditTarget(null);
    } catch (err) {
      setEditError(err.message);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDepartment({ clinicId, departmentId: deleteTarget.id });
      setDeleteTarget(null);
    } catch (err) {
      console.error("[DepartmentsPage] delete failed:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleCopyLink = async (departmentId) => {
    try {
      await navigator.clipboard.writeText(buildCheckInUrl(clinicId, departmentId));
      setCopiedId(departmentId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("[DepartmentsPage] copy failed:", err);
    }
  };

  const specialtyOptions = useMemo(
    () => [{ value: NONE_SPECIALTY, label: "No specialty" }, ...AAMC_SPECIALTIES],
    [],
  );

  if (!clinicId) {
    return (
      <div className="inner">
        <DashboardPageHeader
          title="Departments"
          subtitle="Organize your facility into departments you can message and tag check-ins to."
        />
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Departments"
        subtitle="Organize your facility into departments you can message and tag check-ins to."
      />

      {/* Create */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Add a department
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each department gets its own check-in link, so you can message just the
          patients who checked in there.
        </Typography>

        {createdName != null && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreatedName(null)}>
            Added “{createdName}”.
          </Alert>
        )}
        {createError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
            {createError}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onCreate)}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
            <Controller
              name="name"
              control={control}
              rules={{ required: "Enter a department name." }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Department name"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="specialty"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Specialty" fullWidth>
                  {specialtyOptions.map((s) => (
                    <MenuItem key={s.value || "none"} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <LoadingButton
              type="submit"
              variant="contained"
              loading={creating}
              disabled={!isValid}
              sx={{ flexShrink: 0, mt: { xs: 0, sm: 0.5 } }}
            >
              Add
            </LoadingButton>
          </Stack>
        </Box>
      </Paper>

      {/* List */}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Your departments
        </Typography>

        {loading ? (
          <CircularProgress />
        ) : departments.length === 0 ? (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
            <ApartmentRounded color="disabled" sx={{ fontSize: 40 }} />
            <Typography variant="body2" color="text.secondary">
              No departments yet. Add one above to get started.
            </Typography>
          </Stack>
        ) : (
          <List disablePadding>
            {departments.map((dept, idx) => (
              <React.Fragment key={dept.id}>
                {idx > 0 && <Divider component="li" />}
                <ListItem
                  disableGutters
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title={copiedId === dept.id ? "Copied" : "Copy check-in link"}>
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(dept.id)}
                          aria-label="Copy check-in link"
                        >
                          {copiedId === dept.id ? (
                            <CheckRounded fontSize="small" color="success" />
                          ) : (
                            <ContentCopyRounded fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => setEditTarget(dept)}
                          aria-label="Edit department"
                        >
                          <EditRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(dept)}
                          aria-label="Delete department"
                        >
                          <DeleteOutlineRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={dept.name}
                    secondary={dept.specialty ? getSpecialtyLabel(dept.specialty) : "No specialty"}
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onClose={() => {
          if (!savingEdit) setEditTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Edit department</DialogTitle>
        <Box component="form" onSubmit={handleEditSubmit(onSaveEdit)}>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              {editError && (
                <Alert severity="error" onClose={() => setEditError(null)}>
                  {editError}
                </Alert>
              )}
              <Controller
                name="name"
                control={editControl}
                rules={{ required: "Enter a department name." }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Department name"
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                name="specialty"
                control={editControl}
                render={({ field }) => (
                  <TextField {...field} select label="Specialty" fullWidth>
                    {specialtyOptions.map((s) => (
                      <MenuItem key={s.value || "none"} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={() => setEditTarget(null)} disabled={savingEdit}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={savingEdit}
              disabled={!editIsValid}
            >
              Save
            </LoadingButton>
          </DialogActions>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        title="Delete this department?"
        message={
          deleteTarget
            ? `“${deleteTarget.name}” will be removed. Past check-ins tagged to it are kept, but you won't be able to message this department anymore.`
            : ""
        }
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        loading={deleting}
      />
    </div>
  );
};

export default DepartmentsPage;
